import React from "react";
import { Icon, Text } from "@zorni/components-lib";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import InfoTooltip from "./InfoTooltip";
import styles from "../PartnerAnalyticsDashboard.module.css";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Content sourced from the actual computation (this file / usePartnerAnalyticsDashboard)
// cross-checked against https://docs.mondoviva.com/Da/monitoring/#1-tableau-des-valeurs-de-tracking.
const KPI_INFO_CONTENT = {
  bounce: (
    <>
      <p>
        <strong>Taux de rebond</strong> : part des visites où le visiteur a quitté l'annonce en
        moins de 10 secondes, sans autre interaction.
      </p>
      <p>
        Formule : (nombre de rebonds / nombre total de visites) × 100. Plus le taux est{" "}
        <strong>bas</strong>, meilleur est l'engagement — &lt; 25% Excellent, 25–40% Bon, 40–55%
        Moyen, 55–70% Faible, &gt; 70% Critique.
      </p>
      <p>Seules les visites de moins de 10 secondes comptent comme un rebond.</p>
    </>
  ),
  time: (
    <>
      <p>
        <strong>Temps moyen passé</strong> sur la page de l'annonce, mesuré lors de l'événement
        dédié (pas sur la durée totale de la session).
      </p>
      <p>
        Seuils : &lt; 10s Faible intérêt, 10–30s Intérêt modéré, &gt; 30s Excellent engagement. Le
        chiffre affiché est la vraie moyenne, même si le cadran visuel plafonne à 60s.
      </p>
    </>
  ),
  review: (
    <>
      <p>
        Part des visiteurs ayant consulté l'annonce qui ont <strong>aussi lu les avis clients</strong>.
      </p>
      <p>
        Formule : (nombre de lectures d'avis / nombre de vues de l'annonce) × 100. Plus le taux
        est élevé, plus les visiteurs s'intéressent à votre réputation.
      </p>
    </>
  ),
  favorites: (
    <>
      <p>
        Nombre total d'ajouts aux favoris sur l'établissement/la publication et la période
        sélectionnés.
      </p>
      <p>
        C'est un <strong>volume cumulé</strong> d'actions "ajouter aux favoris", pas un taux — un
        même visiteur peut contribuer plusieurs fois.
      </p>
    </>
  ),
};

// Rounds to at most 2 decimals and drops trailing zeros (40.677967 -> 40.68, 40 -> 40).
const formatPercent = (value) => Number((Number(value) || 0).toFixed(2));

const ReviewReadRateDonut = ({ percentage, compact = false }) => {
  const value = clamp(Number(percentage) || 0, 0, 100);
  const data = [
    { name: "Engagés", value },
    { name: "Autres", value: 100 - value },
  ];
  const COLORS = ["#1e40af", "#3b82f6"];
  const innerRadius = compact ? 70 : 86;
  const outerRadius = compact ? 94 : 112;

  return (
    <div
      className={styles.kpiChartFrame}
      style={{ height: compact ? 244 : 324, marginTop: compact ? 6 : 8 }}
      aria-label={`Avis clients consultés : ${value}%`}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={0}
            dataKey="value"
            startAngle={90}
            endAngle={450}
            isAnimationActive
            animationDuration={900}
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${entry.name}`} fill={COLORS[index]} stroke="none" />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      <div className={styles.kpiCenterValue}>
        <div className={styles.kpiCenterPrimary} style={{ fontSize: compact ? 30 : undefined }}>
          {value}%
        </div>
        <div className={styles.kpiCenterSecondary}>Clients engagés</div>
      </div>
    </div>
  );
};

// Single source of truth for the gauge's bands: pill label/color, needle/dot
// color, and feedback message all come from here so they can never disagree
// at a boundary value. Left-closed / right-open on each threshold (25, 40,
// 55, 70) — e.g. exactly 25 falls into "Bon", not "Excellent" — matching the
// "< 25%" wording in the legend and applied consistently to every threshold.
const BOUNCE_RATE_BANDS = [
  {
    max: 25,
    pillLabel: "Excellent",
    color: "#22c55e",
    feedbackTitle: "Excellent !",
    feedbackText: "Votre annonce est parfaitement optimisée et performante.",
    feedbackColor: "#16a34a",
    feedbackBg: "rgba(240, 253, 244, 0.85)",
    feedbackBorder: "rgba(34, 197, 94, 0.22)",
  },
  {
    max: 40,
    pillLabel: "Bon",
    color: "#a3e635",
    feedbackTitle: "Bon",
    feedbackText: "L'annonce est de bonne qualité globale.",
    feedbackColor: "#4d7c0f",
    feedbackBg: "rgba(247, 254, 231, 0.9)",
    feedbackBorder: "rgba(163, 230, 53, 0.26)",
  },
  {
    max: 55,
    pillLabel: "Moyen",
    color: "#fbbf24",
    feedbackTitle: "Moyen",
    feedbackText: "Des améliorations sont possibles pour optimiser l'annonce.",
    feedbackColor: "#b45309",
    feedbackBg: "rgba(255, 251, 235, 0.92)",
    feedbackBorder: "rgba(251, 191, 36, 0.24)",
  },
  {
    max: 70,
    pillLabel: "Faible",
    color: "#f97316",
    feedbackTitle: "Faible",
    feedbackText: "La qualité de l'annonce est insuffisante, pensez à la retravailler.",
    feedbackColor: "#ea580c",
    feedbackBg: "rgba(255, 247, 237, 0.9)",
    feedbackBorder: "rgba(249, 115, 22, 0.22)",
  },
  {
    max: Infinity,
    pillLabel: "Critique",
    color: "#ef4444",
    feedbackTitle: "Critique",
    feedbackText: "Attention, l'annonce nécessite des modifications urgentes.",
    feedbackColor: "#dc2626",
    feedbackBg: "rgba(254, 242, 242, 0.9)",
    feedbackBorder: "rgba(239, 68, 68, 0.22)",
  },
];

const getBounceRateBand = (rate) =>
  BOUNCE_RATE_BANDS.find((band) => rate < band.max) ?? BOUNCE_RATE_BANDS[BOUNCE_RATE_BANDS.length - 1];

const BounceRateGauge = ({ percentage, previousPercentage }) => {
  const value = clamp(Number(percentage) || 0, 0, 100);
  const previousValue =
    previousPercentage === undefined ? null : clamp(Number(previousPercentage) || 0, 0, 100);
  const delta = previousValue === null ? null : value - previousValue;

  const band = getBounceRateBand(value);

  // Full speedometer-style arc (open at the bottom via startAngle/endAngle
  // below, not via a dead segment): the 5 bands fill the whole 0-100 range,
  // no gray "unused" slice — their widths mirror BOUNCE_RATE_BANDS exactly.
  const ringSegments = [
    { value: 25, color: "#22c55e" }, // 0–25 Excellent
    { value: 15, color: "#a3e635" }, // 25–40 Bon
    { value: 15, color: "#fbbf24" }, // 40–55 Moyen
    { value: 15, color: "#f97316" }, // 55–70 Faible
    { value: 30, color: "#ef4444" }, // 70–100 Critique
  ];

  // Gauge arc spans 260deg (220deg -> -40deg in Recharts' angle space, where
  // 0° sits at 3 o'clock and increasing angle sweeps counter-clockwise).
  const startAngle = 220;
  const endAngle = -40;
  const sweep = startAngle - endAngle; // 260deg
  const maxGauge = 100;
  const needleAngleRecharts = startAngle - (value / maxGauge) * sweep;
  // CSS rotate() lives in screen space (positive = clockwise), and the
  // needle's own shape (see .gaugeNeedle's clip-path) points to 3 o'clock —
  // i.e. Recharts' 0° — at rotate(0deg). Since Recharts' angle grows
  // counter-clockwise, converting to CSS rotation is a straight negation;
  // no extra 90° offset (that was the bug: it aimed the needle a quarter
  // turn off the arc, e.g. into the red band for a mid-"Moyen" score).
  const needleRotation = -needleAngleRecharts;

  return (
    <div className={styles.bounceWrap}>
      <div className={styles.kpiChartStage}>
        <div className={styles.kpiChartFrame} style={{ height: 308, marginTop: 0 }} aria-label={`Taux de rebond : ${formatPercent(value)}%`}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={ringSegments}
                cx="50%"
                cy="53%"
                startAngle={startAngle}
                endAngle={endAngle}
                innerRadius={86}
                outerRadius={112}
                paddingAngle={0}
                dataKey="value"
                isAnimationActive
                animationDuration={900}
              >
                {ringSegments.map((entry, index) => (
                  <Cell key={`seg-${index}`} fill={entry.color} stroke="none" />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>

          <div
            className={styles.kpiCenterValue}
            style={{ justifyContent: "center", paddingBottom: 0, zIndex: 5, transform: "translateY(22%)" }}
          >
            <div className={styles.kpiCenterPrimary} style={{ fontSize: 34, marginTop: 0 }}>
              {formatPercent(value)}%
            </div>
            <div className={styles.gaugeStatusPill} style={{ ["--pill-color"]: band.color, marginTop: 10 }}>
              <span className={styles.gaugeStatusDot} aria-hidden="true" />
              {band.pillLabel}
            </div>
          </div>

          <div
            aria-hidden="true"
            className={styles.gaugeNeedle}
            style={{
              ["--needle-rotate"]: `${needleRotation}deg`,
              ["--needle-color"]: band.color,
            }}
          />

          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              width: 16,
              height: 16,
              borderRadius: 999,
              transform: "translate(-50%, -50%)",
              background: "rgba(148, 163, 184, 0.55)",
              boxShadow: "0 8px 18px rgba(15, 23, 42, 0.18)",
              zIndex: 3,
            }}
          />
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              width: 8,
              height: 8,
              borderRadius: 999,
              transform: "translate(-50%, -50%)",
              background: band.color,
              boxShadow: "0 0 0 2px rgba(255,255,255,0.9)",
              zIndex: 4,
            }}
          />
        </div>
      </div>

      <div className={styles.gaugeMinMaxArc} aria-hidden="true">
        <span className={styles.gaugeMinMaxLeft}>0%</span>
        <span className={styles.gaugeMinMaxRight}>100%</span>
      </div>

      <div className={styles.gaugeLegend} aria-label="Légende des niveaux">
        <span className={styles.gaugeLegendItem}>
          <span className={styles.gaugeLegendDot} style={{ background: "#22c55e" }} aria-hidden="true" /> &lt; 25% Excellent
        </span>
        <span className={styles.gaugeLegendItem}>
          <span className={styles.gaugeLegendDot} style={{ background: "#a3e635" }} aria-hidden="true" /> 25–40% Bon
        </span>
        <span className={styles.gaugeLegendItem}>
          <span className={styles.gaugeLegendDot} style={{ background: "#fbbf24" }} aria-hidden="true" /> 40–55% Moyen
        </span>
        <span className={styles.gaugeLegendItem}>
          <span className={styles.gaugeLegendDot} style={{ background: "#f97316" }} aria-hidden="true" /> 55–70% Faible
        </span>
        <span className={styles.gaugeLegendItem}>
          <span className={styles.gaugeLegendDot} style={{ background: "#ef4444" }} aria-hidden="true" /> &gt; 70% Critique
        </span>
      </div>

      <div
        className={`${styles.timeMessageBox} ${styles.kpiFeedbackBox}`}
        style={{
          ["--time-msg-color"]: band.feedbackColor,
          ["--time-msg-bg"]: band.feedbackBg,
          ["--time-msg-border"]: band.feedbackBorder,
        }}
      >
        <span className={styles.timeMessageIcon} aria-hidden="true">
          ★
        </span>
        <div>
          <div className={styles.timeMessageTitle}>{band.feedbackTitle}</div>
          <div className={styles.timeMessageText}>{band.feedbackText}</div>
        </div>
      </div>
    </div>
  );
};

const AvgTimeSpentProgress = ({ seconds }) => {
  // Real value shown to the user — no upper cap. Only the donut ring's fill
  // percentage is capped at 60s (its visual scale); the number itself must
  // reflect the true API value, e.g. 1273s should read "1273s", not "60s".
  const value = Math.max(Number(seconds) || 0, 0);
  const donutValue = clamp(value, 0, 60);

  const status =
    value > 30
      ? {
          title: "Excellent !",
          text: "Votre contenu retient très bien l'attention.",
          color: "#16a34a",
          bg: "rgba(240, 253, 244, 0.85)",
          border: "rgba(34, 197, 94, 0.22)",
        }
      : value >= 10
        ? {
            title: "Intérêt modéré",
            text: "Votre contenu suscite de l'intérêt, mais peut être optimisé.",
            color: "#ea580c",
            bg: "rgba(255, 247, 237, 0.9)",
            border: "rgba(249, 115, 22, 0.22)",
          }
        : {
            title: "Faible",
            text: "Votre contenu capte peu l'attention. Ajoutez des détails et des visuels.",
            color: "#dc2626",
            bg: "rgba(254, 242, 242, 0.9)",
            border: "rgba(239, 68, 68, 0.22)",
          };

  const donutPercent = clamp((donutValue / 60) * 100, 0, 100);
  const donutCircumference = 2 * Math.PI * 92;
  const donutDashOffset = donutCircumference * (1 - donutPercent / 100);

  // Mock distribution (API not available yet for buckets).
  const bands = [
    { range: "< 10s", label: "Faible", color: "#ef4444" },
    { range: "10–30s", label: "Intérêt modéré", color: "#f97316" },
    { range: "> 30s", label: "Élevé", color: "#22c55e" },
  ];

  return (
    <div className={styles.timeWrap} aria-label={`Temps moyen : ${value} secondes`}>
      <div className={styles.kpiChartStage}>
        <div className={styles.timeDonutFrame} aria-hidden="true">
          <svg width="220" height="220" viewBox="0 0 220 220" className={styles.timeDonutSvg}>
            <defs>
              <linearGradient id="timeDonutGradient" x1="20" y1="30" x2="200" y2="200" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#2563eb" />
                <stop offset="60%" stopColor="#3b82f6" />
                <stop offset="100%" stopColor="#22c55e" />
              </linearGradient>
            </defs>

            <circle cx="110" cy="110" r="92" fill="none" stroke="rgba(226, 232, 240, 0.9)" strokeWidth="16" />
            <circle
              cx="110"
              cy="110"
              r="92"
              fill="none"
              stroke="url(#timeDonutGradient)"
              strokeWidth="16"
              strokeLinecap="round"
              strokeDasharray={donutCircumference}
              strokeDashoffset={donutDashOffset}
              transform="rotate(-90 110 110)"
              style={{ transition: "stroke-dashoffset 900ms cubic-bezier(0.2, 0.9, 0.2, 1)" }}
            />
          </svg>

          <div className={styles.kpiCenterValue} style={{ justifyContent: "center" }}>
            <div className={styles.kpiCenterPrimary}>
              {value}
              <span className={styles.timeSecondsSuffix}>s</span>
            </div>
            <div className={styles.kpiCenterSecondary} style={{ fontSize: 13 }}>
              Moyenne par session
            </div>
          </div>
        </div>
      </div>

      <div className={styles.timeScale} aria-label="Répartition par tranche (barre)">
        <div className={styles.timeScaleTrack}>
          <div className={styles.timeScaleLow} />
          <div className={styles.timeScaleMid} />
          <div className={styles.timeScaleHigh} />
        </div>
      </div>

      <div className={styles.timeBandLegend} aria-label="Répartition par tranche">
        {bands.map((band) => (
          <div key={band.range}>
            
            <div className={styles.timeBandRange} style={{ color: "#0f172a" }}>
              {band.range}
            </div>
            <div className={styles.timeBandLabel}>{band.label}</div>
          </div>
        ))}
      </div>

      <div
        className={`${styles.timeMessageBox} ${styles.kpiFeedbackBox}`}
        style={{
          ["--time-msg-color"]: status.color,
          ["--time-msg-bg"]: status.bg,
          ["--time-msg-border"]: status.border,
        }}
      >
        <span className={styles.timeMessageIcon} aria-hidden="true">
          ★
        </span>
        <div>
          <div className={styles.timeMessageTitle}>{status.title}</div>
          <div className={styles.timeMessageText}>{status.text}</div>
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ kpi, className, compact = false }) => {
  if (!kpi) return null;
  const { key, label, value, hint, rawValue, previousRawValue, unit } = kpi;

  const infoContent = KPI_INFO_CONTENT[key];

  const header = (
    <div className={styles.kpiHeaderRow}>
      <div>
        <Text as="p" size="xs" weight="bold" color="muted" className={styles.kpiLabel}>
          {label}
        </Text>
        {hint ? (
          <Text as="p" size="xs" color="muted">
            {hint}
          </Text>
        ) : null}
      </div>
      {infoContent ? <InfoTooltip title={label}>{infoContent}</InfoTooltip> : null}
    </div>
  );

  if (key === "review") {
    return (
      <article className={`${styles.kpiCard} ${className || ""}`.trim()}>
        {header}
        <div className={styles.kpiBody}>
          <ReviewReadRateDonut percentage={rawValue} compact={compact} />
        </div>
      </article>
    );
  }

  if (key === "bounce") {
    return (
      <article className={`${styles.kpiCard} ${className || ""}`.trim()}>
        {header}
        <div className={styles.kpiBody}>
          <BounceRateGauge percentage={rawValue} previousPercentage={previousRawValue} />
        </div>
      </article>
    );
  }

  if (key === "time") {
    return (
      <article className={`${styles.kpiCard} ${className || ""}`.trim()}>
        {header}
        <div className={styles.kpiBody}>
          <AvgTimeSpentProgress seconds={rawValue} />
        </div>
      </article>
    );
  }

  if (key === "favorites") {
    const displayedLabel =
      compact && typeof label === "string" && label.toLowerCase().includes("favoris")
        ? "Favoris"
        : label;

    return (
      <article
        className={`${styles.kpiCard} ${styles.favoritesCard} ${className || ""}`.trim()}
        data-compact={compact ? "true" : "false"}
      >
        <div className={styles.favoritesHeader}>
          <div className={styles.favoritesHeaderLeft}>
            <Text as="p" size="xs" weight="bold" color="muted" className={styles.kpiLabel}>
              {displayedLabel}
            </Text>
          </div>
          <InfoTooltip title={label}>{KPI_INFO_CONTENT.favorites}</InfoTooltip>
        </div>

        <div className={styles.favoritesCenter}>
          <Text as="p" size={compact ? "xxl" : "xxxl"} weight="bold" className={styles.favoritesValue}>
            {value}
            {unit ? <span className={styles.favoritesUnit}>{unit}</span> : null}
          </Text>

          {hint ? (
            <Text as="p" size="xs" color="muted" className={styles.favoritesHint}>
              {hint}
            </Text>
          ) : null}

          <span className={styles.favoritesHeart} aria-hidden="true">
            <Icon name="iconFavoriteRegular" size={compact ? "md" : "lg"} color="muted" />
          </span>
        </div>
      </article>
    );
  }

  return (
    <article className={`${styles.kpiCard} ${className || ""}`.trim()}>
      <div className={styles.kpiBody}>
        <Text as="p" size="xs" weight="bold" color="muted" className={styles.kpiLabel}>
          {label}
        </Text>
        <Text as="p" size="xxl" weight="bold" className={styles.kpiValue}>
          {value}
          {unit ? <span style={{ fontSize: 18, fontWeight: 700, color: "#334155" }}>{unit}</span> : null}
        </Text>
        <Text as="p" size="xs" color="muted">
          {hint}
        </Text>
      </div>
    </article>
  );
};

export default KpiCard;
